import React, { useState, useEffect, Suspense } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

// Lazy load the calculator component
const RoiCalculator = React.lazy(() => import('./RoiCalculator'));

const RoiCalculatorSkeleton = () => (
  <div className="animate-pulse">
    <div className="space-y-4">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-10 bg-gray-200 rounded"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

const RoiCalculatorPanel = ({ market = 'malta' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [roiResult, setRoiResult] = useState(null);
  const [isRequestingReport, setIsRequestingReport] = useState(false);
  const [reportRequested, setReportRequested] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    phone: ''
  });

  // Session storage persistence
  useEffect(() => {
    const wasOpen = sessionStorage.getItem('roi-panel-open') === 'true';
    setIsOpen(wasOpen);
  }, []);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    sessionStorage.setItem('roi-panel-open', newState.toString());
    
    if (newState && !hasOpened) {
      setHasOpened(true);
      // Fire GA4 event: roi_panel_open
      if (window.gtag) {
        window.gtag('event', 'roi_panel_open', {
          'event_category': 'engagement',
          'event_label': window.location.pathname
        });
      }
    }
  };

  const handleResultChange = (result) => {
    setRoiResult(result);
    
    // Fire GA4 event: roi_result_viewed
    if (window.gtag) {
      window.gtag('event', 'roi_result_viewed', {
        'event_category': 'engagement',
        'value': result.roiPercentage,
        'event_label': `${result.roiPercentage}% ROI`
      });
    }
  };

  const handleCtaClick = (ctaType) => {
    // Fire GA4 event: roi_cta_click
    if (window.gtag) {
      window.gtag('event', 'roi_cta_click', {
        'event_category': 'conversion',
        'event_label': ctaType
      });
    }
  };

  const handleRequestReport = () => {
    if (!roiResult) return;
    setShowEmailForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!roiResult) return;
    
    setIsRequestingReport(true);
    
    try {
      const reportData = {
        // User contact details
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        phone: formData.phone,
        
        // User inputs
        clients: roiResult.breakdown?.inputs?.clients || 0,
        payslipsPerMonth: roiResult.breakdown?.inputs?.payslipsPerMonth || 0,
        hourlyRate: roiResult.breakdown?.inputs?.hourlyRate || 0,
        currentSoftwareMonthlyCost: roiResult.breakdown?.inputs?.currentSoftwareMonthlyCost || 0,
        plan: roiResult.breakdown?.inputs?.plan || 'basic',
        scenario: roiResult.breakdown?.inputs?.scenario || 'realistic',
        market: market,
        
        // ROI Results
        roiPercentage: roiResult.roiPercentage,
        netSavings: roiResult.netSavings,
        monthlyHoursSaved: roiResult.monthlyHoursSaved,
        yearlyTimeSavings: roiResult.yearlyTimeSavings,
        breakEvenMonths: roiResult.breakEvenMonths,
        
        // Detailed breakdown
        breakdown: roiResult.breakdown,
        
        // Page context
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };

      const response = await fetch('/api/roi-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        setReportRequested(true);
        setShowEmailForm(false);
        
        // Fire GA4 event: roi_report_requested
        if (window.gtag) {
          window.gtag('event', 'roi_report_requested', {
            'event_category': 'conversion',
            'event_label': 'ROI Email Report',
            'value': parseFloat(roiResult.roiPercentage)
          });
        }
      } else {
        throw new Error('Failed to request report');
      }
    } catch (error) {
      console.error('Error requesting ROI report:', error);
      alert('Sorry, there was an error requesting your report. Please try again.');
    } finally {
      setIsRequestingReport(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Closed State - Teaser Card */}
        {!isOpen && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Curious about your ROI?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              See how much profit your bureau gains by switching to Buddy before January.
            </p>
            <button
              onClick={handleToggle}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-advisor-900 hover:bg-advisor-700 transition-colors duration-200"
              aria-expanded={isOpen}
              aria-controls="roi-calculator-panel"
            >
              Calculate my ROI
              <ChevronDownIcon className="ml-2 h-5 w-5" />
            </button>
          </div>
        )}

        {/* Open State - Calculator */}
        {isOpen && (
          <div 
            id="roi-calculator-panel"
            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
            style={{ contentVisibility: 'auto' }}
          >
            {/* Panel Header */}
            <div className="px-8 py-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    ROI Calculator
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Most firms see 4–12× ROI in year one.
                  </p>
                </div>
                <button
                  onClick={handleToggle}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close ROI calculator"
                >
                  <ChevronDownIcon className="h-5 w-5 text-gray-500 rotate-180" />
                </button>
              </div>
            </div>

            {/* Calculator Content */}
            <div className="px-8 py-6">
              <Suspense fallback={<RoiCalculatorSkeleton />}>
                <RoiCalculator 
                  onResultChange={handleResultChange}
                  market={market}
                />
              </Suspense>

              {/* Result Display & CTAs */}
              {roiResult && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-advisor-900 mb-2">
                      Your estimated ROI: {roiResult.roiPercentage}%
                    </div>
                    <p className="text-lg text-gray-600">
                      Switching before January typically saves {roiResult.monthlyHoursSaved?.toFixed(1) || 0} hours/month and €{roiResult.yearlyTimeSavings?.toFixed(0) || 0}/year.
                    </p>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="https://calendly.com/buddy-cs/mt-accountants-migration"
                      onClick={() => handleCtaClick('book_migration')}
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-advisor-900 hover:bg-advisor-700 transition-colors"
                    >
                      Book my migration slot
                    </a>
                    <a
                      href="#videos"
                      onClick={() => handleCtaClick('watch_demo')}
                      className="inline-flex items-center justify-center px-6 py-3 border border-advisor-900 text-base font-medium rounded-lg text-advisor-900 bg-white hover:bg-advisor-50 transition-colors"
                    >
                      Watch the 15-min demo
                    </a>
                  </div>

                  {/* ROI Report Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        📊 Want a detailed ROI report?
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Get a professional PDF report with your calculations, perfect for sharing with decision makers.
                      </p>
                      
                      {!reportRequested && !showEmailForm && (
                        <button
                          onClick={handleRequestReport}
                          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-advisor-600 hover:bg-advisor-700 transition-colors"
                        >
                          📧 Send me the ROI Report
                        </button>
                      )}

                      {showEmailForm && !reportRequested && (
                        <div className="max-w-md mx-auto">
                          <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                                  First Name *
                                </label>
                                <input
                                  type="text"
                                  id="firstName"
                                  value={formData.firstName}
                                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-advisor-500 focus:border-advisor-500"
                                />
                              </div>
                              <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                                  Last Name *
                                </label>
                                <input
                                  type="text"
                                  id="lastName"
                                  value={formData.lastName}
                                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-advisor-500 focus:border-advisor-500"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address *
                              </label>
                              <input
                                type="email"
                                id="email"
                                value={formData.email}
                                onChange={(e) => handleFormChange('email', e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-advisor-500 focus:border-advisor-500"
                              />
                            </div>
                            
                            <div>
                              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                                Company/Firm Name *
                              </label>
                              <input
                                type="text"
                                id="companyName"
                                value={formData.companyName}
                                onChange={(e) => handleFormChange('companyName', e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-advisor-500 focus:border-advisor-500"
                              />
                            </div>
                            
                            <div>
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number (optional)
                              </label>
                              <input
                                type="tel"
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => handleFormChange('phone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-advisor-500 focus:border-advisor-500"
                              />
                            </div>
                            
                            <div className="flex gap-3 pt-2">
                              <button
                                type="button"
                                onClick={() => setShowEmailForm(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={isRequestingReport}
                                className="flex-1 px-4 py-2 bg-advisor-600 text-white rounded-lg hover:bg-advisor-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                              >
                                {isRequestingReport ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                  </>
                                ) : (
                                  'Send Report'
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {reportRequested && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-center">
                            <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-green-800 font-medium">
                              Report requested! Check your email in a few minutes.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoiCalculatorPanel;
